/**
 * Number and time formatting, in the units an operator reads.
 *
 * The rule throughout: a value that is genuinely unknown formats as an em dash, never
 * as `0` or `-`. A dashboard that prints 0 for "we could not count it" is stating
 * something false about the system.
 */

const EM_DASH = '—'

/** A latency, kept in the unit it was measured in until seconds make it readable. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return EM_DASH
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 10_000) return `${(ms / 1000).toFixed(2)} s`
  return `${(ms / 1000).toFixed(1)} s`
}

/** Compact for a stat tile: 1,284 / 12.9K / 4.2M. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EM_DASH
  if (Math.abs(value) < 1000) return String(value)
  if (Math.abs(value) < 1_000_000)
    return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`
  return `${(value / 1_000_000).toFixed(1)}M`
}

/**
 * An availability figure with enough decimals to be worth reading.
 *
 * 99.9 and 100 are very different claims, so a value just short of 100 keeps two
 * decimals rather than rounding up to a perfect score it did not earn.
 */
export function formatPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return EM_DASH
  if (pct === 100) return '100%'
  if (pct > 99.9) return `${pct.toFixed(2)}%`
  return `${pct.toFixed(1)}%`
}

/** A duration as a span: 45s, 12m, 3h 20m, 2d 4h. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return EM_DASH
  if (seconds < 60) return `${Math.round(seconds)}s`

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  const restMinutes = minutes % 60
  if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`

  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return restHours ? `${days}d ${restHours}h` : `${days}d`
}

/** "just now" / "14s ago" / "3m ago", from a unix-seconds timestamp. */
export function formatRelative(
  timestamp: number | null | undefined,
  now: number = Date.now() / 1000,
): string {
  if (timestamp === null || timestamp === undefined) return EM_DASH
  const delta = Math.max(0, Math.round(now - timestamp))
  if (delta < 3) return 'just now'
  return `${formatDuration(delta)} ago`
}

/** Wall-clock time for a tooltip, where the exact moment matters. */
export function formatTime(timestamp: number | null | undefined): string {
  if (timestamp === null || timestamp === undefined) return EM_DASH
  return new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function formatDateTime(timestamp: number | null | undefined): string {
  if (timestamp === null || timestamp === undefined) return EM_DASH
  return new Date(timestamp * 1000).toLocaleString()
}

/** A backoff or TTL from the queue snapshot, which arrives in milliseconds. */
export function formatDelay(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return EM_DASH
  return formatDuration(ms / 1000)
}

/** A queue family key (`project_creation`) as a heading ("Project creation"). */
export function humanise(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export { EM_DASH }
